import { useEffect } from 'react';
import type { Camp, AvailabilityStatus } from '../../../mocks/camps';
import MomReviews from './MomReviews';

interface CampDetailModalProps {
  camp: Camp | null;
  onClose: () => void;
}

const availabilityConfig: Record<AvailabilityStatus, { bg: string; text: string; border: string; dot: string; icon: string; label: string }> = {
  'Spots Available':           { bg: 'bg-emerald-50',  text: 'text-emerald-700', border: 'border-emerald-200', dot: 'bg-emerald-500', icon: 'ri-checkbox-circle-fill', label: 'Spots Available' },
  'Limited Spots':             { bg: 'bg-amber-50',    text: 'text-amber-700',   border: 'border-amber-200',   dot: 'bg-amber-500',   icon: 'ri-error-warning-fill',   label: 'Limited Spots' },
  'Waitlist':                  { bg: 'bg-rose-50',     text: 'text-rose-600',    border: 'border-rose-200',    dot: 'bg-rose-400',    icon: 'ri-time-fill',            label: 'Waitlist' },
  'Registration Opens Soon':   { bg: 'bg-sky-50',      text: 'text-sky-700',     border: 'border-sky-200',     dot: 'bg-sky-400',     icon: 'ri-calendar-event-fill',  label: 'Registration Opens Soon' },
  'Unknown — Check with Camp': { bg: 'bg-gray-100',    text: 'text-gray-500',    border: 'border-gray-200',    dot: 'bg-gray-400',    icon: 'ri-question-fill',        label: 'Unknown — Check with Camp' },
};

export default function CampDetailModal({ camp, onClose }: CampDetailModalProps) {
  useEffect(() => {
    if (camp) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [camp]);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [onClose]);

  if (!camp) return null;

  const avail = availabilityConfig[camp.availability];

  const durationLabel =
    camp.duration === 'full-day' ? 'Full-Day' :
    camp.duration === 'half-day' ? 'Half-Day' :
    camp.duration === 'overnight' ? 'Overnight' :
    camp.duration;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm"></div>

      {/* Modal */}
      <div
        className="relative w-full max-w-2xl max-h-[90vh] bg-white rounded-3xl shadow-2xl overflow-hidden flex flex-col animate-modal-in"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Hero image */}
        <div className="relative h-60 flex-shrink-0 bg-gray-900 overflow-hidden">
          <img
            src={camp.photo}
            alt={camp.name}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>

          {/* Availability badge — top left */}
          <div className="absolute top-3 left-3">
            <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap shadow-md border ${avail.bg} ${avail.text} ${avail.border}`}>
              <span className={`w-2 h-2 rounded-full flex-shrink-0 ${avail.dot}`}></span>
              {avail.label}
            </span>
          </div>

          {/* Mama Preferred Badge */}
          {camp.discount && (
            <div className="absolute top-3 right-14">
              <span className="bg-white/90 text-rose-500 text-xs font-semibold px-2.5 py-1 rounded-full shadow-sm whitespace-nowrap flex items-center gap-1 border border-rose-100 tracking-wide">
                <span className="text-rose-400">✦</span>
                <span>Mama Preferred</span>
              </span>
            </div>
          )}

          {/* Close button */}
          <button
            onClick={onClose}
            className="absolute top-3 right-3 w-9 h-9 bg-white/90 rounded-full flex items-center justify-center hover:bg-white transition-colors cursor-pointer shadow-md"
          >
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-close-line text-xl text-gray-700"></i>
            </div>
          </button>

          {/* Camp name overlay */}
          <div className="absolute bottom-4 left-5 right-16">
            <h2 className="text-2xl font-bold text-white leading-tight drop-shadow">{camp.name}</h2>
            <div className="flex items-center gap-1 mt-1">
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-map-pin-line text-white/90 text-sm"></i>
              </div>
              <span className="text-white/90 text-sm">{camp.city}</span>
            </div>
          </div>
        </div>

        {/* Quick stats bar */}
        <div className="flex items-center justify-around bg-gradient-to-r from-pink-50 to-orange-50 border-b border-gray-100 px-4 py-3 flex-shrink-0">
          <div className="flex flex-col items-center">
            <div className="w-5 h-5 flex items-center justify-center mb-0.5">
              <i className="ri-user-line text-pink-500 text-base"></i>
            </div>
            <span className="text-xs text-gray-500">Ages</span>
            <span className="text-sm font-bold text-gray-800">{camp.ageRange}</span>
          </div>
          <div className="w-px h-10 bg-gray-200"></div>
          <div className="flex flex-col items-center">
            <div className="w-5 h-5 flex items-center justify-center mb-0.5">
              <i className="ri-money-dollar-circle-line text-orange-500 text-base"></i>
            </div>
            <span className="text-xs text-gray-500">Price</span>
            <span className="text-sm font-bold text-gray-800">{camp.priceRange}</span>
          </div>
          <div className="w-px h-10 bg-gray-200"></div>
          <div className="flex flex-col items-center">
            <div className="w-5 h-5 flex items-center justify-center mb-0.5">
              <i className="ri-time-line text-yellow-500 text-base"></i>
            </div>
            <span className="text-xs text-gray-500">Schedule</span>
            <span className="text-sm font-bold text-gray-800">{durationLabel}</span>
          </div>
          <div className="w-px h-10 bg-gray-200"></div>
          <div className="flex flex-col items-center">
            <div className="w-5 h-5 flex items-center justify-center mb-0.5">
              <i className="ri-calendar-line text-green-500 text-base"></i>
            </div>
            <span className="text-xs text-gray-500">Weeks</span>
            <span className="text-sm font-bold text-gray-800">{camp.availableWeeks.length} available</span>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-5">

          {/* Availability Status Banner */}
          <div className={`flex items-center gap-3 px-4 py-3 rounded-2xl border ${avail.bg} ${avail.border}`}>
            <div className={`w-8 h-8 flex items-center justify-center rounded-full ${avail.bg} flex-shrink-0`}>
              <i className={`${avail.icon} text-lg ${avail.text}`}></i>
            </div>
            <div>
              <p className={`text-xs font-semibold uppercase tracking-wide ${avail.text} opacity-70`}>Enrollment Status</p>
              <p className={`text-sm font-bold ${avail.text}`}>{avail.label}</p>
            </div>
            <span className={`ml-auto w-2.5 h-2.5 rounded-full flex-shrink-0 ${avail.dot} animate-pulse`}></span>
          </div>

          {/* Description */}
          <div>
            <h3 className="text-base font-bold text-gray-800 mb-2 flex items-center gap-2">
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-file-text-line text-pink-500"></i>
              </div>
              About This Camp
            </h3>
            <p className="text-gray-600 text-sm leading-relaxed">{camp.description}</p>
          </div>

          {/* Details grid */}
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-gray-50 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-1">
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-map-pin-2-line text-pink-500 text-sm"></i>
                </div>
                <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Location</span>
              </div>
              <span className="text-sm font-bold text-gray-800">{camp.city}</span>
            </div>

            <div className="bg-gray-50 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-1">
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-sun-line text-orange-500 text-sm"></i>
                </div>
                <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Category</span>
              </div>
              <span className="text-sm font-bold text-gray-800 capitalize">{camp.category.replace('-', ' ')}</span>
            </div>

            <div className="bg-gray-50 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-1">
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-time-line text-yellow-500 text-sm"></i>
                </div>
                <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Day Type</span>
              </div>
              <span className="text-sm font-bold text-gray-800">{durationLabel}</span>
            </div>

            <div className="bg-gray-50 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-1">
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-user-line text-green-500 text-sm"></i>
                </div>
                <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Age Range</span>
              </div>
              <span className="text-sm font-bold text-gray-800">Ages {camp.ageRange}</span>
            </div>
          </div>

          {/* Weeks available */}
          <div>
            <h3 className="text-base font-bold text-gray-800 mb-2 flex items-center gap-2">
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-calendar-check-line text-pink-500"></i>
              </div>
              Weeks Available
            </h3>
            <div className="flex flex-wrap gap-2">
              {camp.availableWeeks.map((w) => (
                <span key={w} className="bg-pink-50 text-pink-700 text-xs font-semibold px-3 py-1.5 rounded-full border border-pink-100">
                  Week {w}
                </span>
              ))}
            </div>
          </div>

          {/* Divider */}
          <div className="border-t border-gray-100"></div>

          {/* Mom Reviews */}
          <MomReviews campId={camp.id} />
        </div>

        {/* Footer CTA */}
        <div className="flex-shrink-0 px-6 py-4 border-t border-gray-100 bg-white flex gap-3">
          <a
            href={camp.registrationLink ?? camp.website}
            target="_blank"
            rel="noopener noreferrer"
            className="flex-1 bg-pink-500 text-white text-sm font-bold py-3 rounded-xl hover:bg-pink-600 transition-colors text-center whitespace-nowrap"
          >
            <i className="ri-external-link-line mr-2"></i>
            Register Now
          </a>
          <a
            href={camp.website}
            target="_blank"
            rel="noopener noreferrer"
            className="px-5 bg-gray-100 text-gray-700 text-sm font-semibold py-3 rounded-xl hover:bg-gray-200 transition-colors text-center whitespace-nowrap"
          >
            Visit Website
          </a>
        </div>
      </div>

      <style>{`
        @keyframes modal-in {
          from { opacity: 0; transform: scale(0.95) translateY(16px); }
          to   { opacity: 1; transform: scale(1) translateY(0); }
        }
        .animate-modal-in {
          animation: modal-in 0.25s ease-out both;
        }
      `}</style>
    </div>
  );
}
